import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { 
  FaArrowLeft, FaFilePdf, FaDownload, FaEnvelope, 
  FaCheckCircle, FaTimes, FaSpinner, FaEdit,
  FaTrash, FaBuilding, FaUser, FaPhone, FaEnvelope as FaEnvelopeIcon,
  FaCalendarAlt, FaDollarSign, FaTruck, FaWeightHanging,
  FaMapMarkerAlt, FaShieldAlt, FaInfoCircle, FaClipboardCheck,
  FaCopy, FaWhatsapp, FaPlus
} from 'react-icons/fa';
import AdminLayout from '../layouts/AdminLayout';
import { quotes, siteInspections, siteInspectors } from '../api';
import QuotePDFButton from '../components/QuotePDFButton';
import QuotePDF from '../components/QuotePDF';
import ProformaInvoiceButton from '../components/ProformaInvoiceButton';
import { API_URL } from '../config';
import ConfirmDialog from '../components/ConfirmDialog';
import { effectiveQuoteStatus } from '../utils/quoteStatus';

function QuoteDetailPage({ onLogout }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [quote, setQuote] = useState(null);
  const [user, setUser] = useState({});
  const [processing, setProcessing] = useState(false);

  // ✅ PHASE 3 / 3.5: Site inspection state
  const [inspections, setInspections] = useState([]);
  const [inspectionsLoading, setInspectionsLoading] = useState(false);
  const [requestingInspection, setRequestingInspection] = useState(false);
  const [inspectionEmail, setInspectionEmail] = useState('');
  const [showInspectionModal, setShowInspectionModal] = useState(false);

  // 3.5: type dropdown + custom questions + inspector picker
  const [inspectionType, setInspectionType] = useState('standard');
  const [customQuestion, setCustomQuestion] = useState('');
  const [customQuestions, setCustomQuestions] = useState([]);
  const [inspectors, setInspectors] = useState([]);
  const [selectedInspector, setSelectedInspector] = useState('');
  const [targetDate, setTargetDate] = useState('');
  const [generatedLink, setGeneratedLink] = useState('');
  const [generatedReportLink, setGeneratedReportLink] = useState('');
  const [requestDone, setRequestDone] = useState(false);

  // ✅ Delete inspection confirmation modal (replaces window.confirm)
  const [showDeleteInspectionModal, setShowDeleteInspectionModal] = useState(false);
  const [deleteInspectionTarget, setDeleteInspectionTarget] = useState(null);
  const [deletingInspection, setDeletingInspection] = useState(false);

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    setUser(userData);
    fetchQuote();
  }, [id]);

  const fetchQuote = async () => {
    setLoading(true);
    try {
      const response = await quotes.getOne(id);
      if (response.success) {
        setQuote(response.quote);
        setInspectionEmail(response.quote.contact_email || '');
        fetchInspections();
      } else {
        toast.error('Failed to load quote');
        navigate('/quotes');
      }
    } catch (err) {
      console.error('Error fetching quote:', err);
      toast.error('Failed to load quote');
      navigate('/quotes');
    } finally {
      setLoading(false);
    }
  };

  // ✅ PHASE 3: Fetch site inspections for this quote
  const fetchInspections = async () => {
    setInspectionsLoading(true);
    try {
      const response = await siteInspections.getByQuote(id);
      if (response.success) {
        setInspections(response.inspections || []);
      }
    } catch (err) {
      console.error('Error fetching inspections:', err);
    } finally {
      setInspectionsLoading(false);
    }
  };

  // ✅ PHASE 3.5: Open request modal + load inspectors
  const openInspectionModal = async () => {
    setShowInspectionModal(true);
    setInspectionType('standard');
    setCustomQuestions([]);
    setCustomQuestion('');
    setSelectedInspector('');
    setTargetDate('');
    setGeneratedLink('');
    setGeneratedReportLink('');
    setRequestDone(false);
    try {
      const response = await siteInspectors.getAll({ active: true });
      if (response.success) {
        setInspectors(response.inspectors || []);
      }
    } catch (err) {
      console.error('Error fetching inspectors:', err);
    }
  };

  const addCustomQuestion = () => {
    if (!customQuestion.trim()) return;
    setCustomQuestions(prev => [...prev, customQuestion.trim()]);
    setCustomQuestion('');
  };

  const removeCustomQuestion = (idx) => {
    setCustomQuestions(prev => prev.filter((_, i) => i !== idx));
  };

  const copyText = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      toast.success('Link copied to clipboard!');
    }).catch(() => {
      const ta = document.createElement('textarea');
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      toast.success('Link copied to clipboard!');
    });
  };

  const formatPhoneForWhatsApp = (phone) => {
    const digits = (phone || '').replace(/\D/g, '');
    if (!digits) return '';
    if (digits.startsWith('27')) return digits;
    if (digits.length === 9) return `27${digits}`;
    if (digits.length === 10 && digits.startsWith('0')) return `27${digits.substring(1)}`;
    return digits;
  };

  // ✅ Build links from a saved inspection record's token.
  // Uses the frontend's own origin, so it's correct in dev (localhost:3000)
  // and in prod (FRONTEND_URL / app.onepurposelogistics.com).
  const getInspectionLink = (inspection) =>
    `${window.location.origin}/inspection/${inspection.token}`;

  const getReportLink = (inspection) =>
    `${window.location.origin}/inspection/${inspection.token}/report`;

  // ✅ Email the inspection link for an existing record
  const emailInspectionLink = (inspection) => {
    const link = getInspectionLink(inspection);
    const subject = encodeURIComponent(`Site Inspection Request — ${quote?.quote_number || ''}`);
    const body = encodeURIComponent(
      `Dear ${quote?.customer_name || 'Customer'},\n\n` +
      `Please complete the short site inspection form for quote ${quote?.quote_number || ''}:\n\n${link}\n\n` +
      `The link is valid for 72 hours.\n\nKind regards,\nOne Purpose Logistics`
    );
    window.location.href = `mailto:${inspection.email_sent_to || quote?.contact_email || ''}?subject=${subject}&body=${body}`;
  };

  // ✅ WhatsApp the inspection link for an existing record (to the customer)
  const whatsappInspectionLink = (inspection) => {
    const link = getInspectionLink(inspection);
    const wa = formatPhoneForWhatsApp(quote?.contact_phone);
    if (!wa) {
      toast.error('No customer phone number on this quote.');
      return;
    }
    const msg = encodeURIComponent(
      `Hi ${quote?.contact_person || quote?.customer_name || ''},\n\n` +
      `One Purpose Logistics needs a quick site inspection to prepare an accurate quote for ${quote?.quote_number}. Please complete this short form:\n\n${link}\n\nThank you!`
    );
    window.open(`https://wa.me/${wa}?text=${msg}`, '_blank');
  };

  // ✅ WhatsApp the report link for an existing inspector record (to the inspector)
  const whatsappInspectorReportLink = (inspection) => {
    const link = getReportLink(inspection);
    const wa = formatPhoneForWhatsApp(inspection.inspector_phone);
    if (!wa) {
      toast.error('This inspector record has no phone number saved.');
      return;
    }
    const msg = encodeURIComponent(
      `Hi ${inspection.inspector_name || 'Inspector'},\n\n` +
      `You have been assigned a site inspection for quote ${quote?.quote_number}. Target date: ${inspection.target_date || 'to be confirmed'}.\n\n` +
      `Complete your rigging site report here:\n${link}\n\nThank you!`
    );
    window.open(`https://wa.me/${wa}?text=${msg}`, '_blank');
  };

  const whatsappToCustomer = () => {
    if (!generatedLink) {
      toast.error('Create the inspection first.');
      return;
    }
    const wa = formatPhoneForWhatsApp(quote?.contact_phone);
    if (!wa) {
      toast.error('No customer phone number on this quote.');
      return;
    }
    const msg = encodeURIComponent(
      `Hi ${quote?.contact_person || quote?.customer_name || ''},\n\n` +
      `One Purpose Logistics needs a quick site inspection to prepare an accurate quote for ${quote?.quote_number}. Please complete this short form:\n\n${generatedLink}\n\nThank you!`
    );
    window.open(`https://wa.me/${wa}?text=${msg}`, '_blank');
  };

  const whatsappToInspector = () => {
    if (!generatedReportLink || !selectedInspector) {
      toast.error('Create the inspector request first and select an inspector.');
      return;
    }
    const inspector = inspectors.find(i => i.id === selectedInspector);
    const wa = formatPhoneForWhatsApp(inspector?.telephone);
    if (!wa) {
      toast.error('This inspector has no phone number saved.');
      return;
    }
    const msg = encodeURIComponent(
      `Hi ${inspector?.name},\n\n` +
      `You have been assigned a site inspection for quote ${quote?.quote_number}. Target date: ${targetDate || 'to be confirmed'}.\n\n` +
      `Complete your rigging site report here:\n${generatedReportLink}\n\nThank you!`
    );
    window.open(`https://wa.me/${wa}?text=${msg}`, '_blank');
  };

  // ✅ PHASE 3.5: Request site inspection (standard | custom | inspector)
  const handleRequestInspection = async () => {
    if (inspectionType !== 'inspector' && !inspectionEmail) {
      toast.error('Please enter an email address to send the inspection link to.');
      return;
    }
    if (inspectionType === 'inspector' && !selectedInspector) {
      toast.error('Please select a site inspector.');
      return;
    }

    setRequestingInspection(true);
    try {
      const payload = {
        type: inspectionType,
        email: inspectionEmail
      };

      if (inspectionType === 'custom') {
        payload.custom_questions = customQuestions.map((q, idx) => ({
          key: `custom_${idx}`,
          label: q,
          type: 'text'
        }));
      }

      if (inspectionType === 'inspector') {
        payload.inspector_id = selectedInspector;
        payload.target_date = targetDate;
      }

      const response = await siteInspections.request(id, payload);

      if (response.success) {
        setGeneratedLink(response.links?.inspection_link || '');
        setGeneratedReportLink(response.links?.report_link || '');
        setRequestDone(true);

        if (inspectionType === 'inspector') {
          toast.success('👷 Inspector requirement created!');
        } else {
          toast.success('📋 Site inspection requested!');
        }
        fetchInspections();
      } else {
        toast.error(response.error || 'Failed to request inspection');
      }
    } catch (err) {
      console.error('Error requesting inspection:', err);
      toast.error('Failed to request inspection');
    } finally {
      setRequestingInspection(false);
    }
  };

  // ✅ PHASE 3: Delete an inspection record (opens styled confirmation modal)
  const handleDeleteInspection = (inspection) => {
    setDeleteInspectionTarget(inspection);
    setShowDeleteInspectionModal(true);
  };

  const confirmDeleteInspection = async () => {
    if (!deleteInspectionTarget) return;
    setDeletingInspection(true);
    try {
      const response = await siteInspections.remove(deleteInspectionTarget.id);
      if (response.success) {
        toast.success('Inspection deleted');
        setShowDeleteInspectionModal(false);
        setDeleteInspectionTarget(null);
        fetchInspections();
      } else {
        toast.error(response.error || 'Failed to delete inspection');
      }
    } catch (err) {
      console.error('Error deleting inspection:', err);
      toast.error('Failed to delete inspection');
    } finally {
      setDeletingInspection(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      'draft': 'bg-gray-100 text-gray-600',
      'sent': 'bg-blue-100 text-blue-600',
      'approved': 'bg-green-100 text-green-600',
      'rejected': 'bg-red-100 text-red-600',
      'cancelled': 'bg-gray-100 text-gray-400',
      'client_request': 'bg-orange-100 text-orange-600',
      'client_accepted': 'bg-green-100 text-green-700',
      'expired': 'bg-red-100 text-red-600',
      'void': 'bg-gray-200 text-gray-700'
    };
    return `px-2 py-1 rounded-full text-xs font-semibold ${styles[status] || 'bg-gray-100 text-gray-600'}`;
  };

  const getStatusLabel = (status) => {
    const labels = {
      'draft': '📝 Draft',
      'sent': '📤 Sent',
      'approved': '✅ Approved',
      'rejected': '❌ Rejected',
      'cancelled': '🚫 Cancelled',
      'expired': '⛔ Expired',
      'void': '🚫 Voided'
    };
    return labels[status] || status;
  };

  const getTypeLabel = (type) => {
    const labels = {
      'abnormal': '🚛 Abnormal',
      'minerals': '⛏️ Minerals',
      'agri': '🌾 Agri',
      'general': '📋 General'
    };
    return labels[type] || type;
  };

  const formatCurrency = (amount) => {
    if (!amount) return 'R 0.00';
    return `R ${parseFloat(amount).toFixed(2)}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-ZA', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric'
    });
  };

  // ✅ HANDLE SEND QUOTE - Opens email with PDF attached
  const handleSendQuote = () => {
    if (!quote) return;

    const subject = encodeURIComponent(`Quotation: ${quote.quote_number} - ${quote.customer_name || 'Customer'}`);
    const body = encodeURIComponent(
      `Dear ${quote.customer_name || 'Customer'},\n\n` +
      `Please find attached our quotation ${quote.quote_number} for your review.\n\n` +
      `Quotation Summary:\n` +
      `- Quote Number: ${quote.quote_number}\n` +
      `- Type: ${getTypeLabel(quote.quote_type)}\n` +
      `- Total Amount: ${formatCurrency(quote.grand_total)}\n` +
      `- Payment Terms: ${quote.payment_terms || '30 Days'}\n` +
      `- Valid Until: ${formatDate(quote.expiry_date)}\n\n` +
      `Please let us know if you have any questions.\n\n` +
      `Kind regards,\n` +
      `One Purpose Logistics`
    );
    
    window.location.href = `mailto:${quote.contact_email || ''}?subject=${subject}&body=${body}`;
    updateQuoteStatus('sent');
    toast.info('📧 Email client opened. Please attach the PDF manually.');
  };

  // ✅ UPDATE QUOTE STATUS - uses dedicated /send endpoint to avoid data loss
  const updateQuoteStatus = async (status) => {
    try {
      let response;
      if (status === 'sent') {
        response = await quotes.send(id);
      } else {
        response = await quotes.update(id, { status });
      }
      if (response.success) {
        setQuote(prev => ({ ...prev, status }));
        toast.success(`✅ Quote ${status} successfully!`);
        fetchQuote();
      }
    } catch (err) {
      console.error('Error updating quote status:', err);
    }
  };

  // ✅ HANDLE APPROVE - Creates job
  const [approveConfirmOpen, setApproveConfirmOpen] = useState(false);

  const handleApprove = () => setApproveConfirmOpen(true);

  const performApprove = async () => {
    setProcessing(true);
    try {
      const response = await quotes.approve(id);
      if (response.success) {
        toast.success(`✅ Quote ${quote.quote_number} approved! Job ${response.job.job_number} created.`);
        fetchQuote();
      } else {
        toast.error(response.error || 'Failed to approve quote');
      }
    } catch (err) {
      console.error('Error approving quote:', err);
      toast.error('Failed to approve quote');
    } finally {
      setProcessing(false);
    }
  };

  // ✅ HANDLE DELETE
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  const handleDelete = () => setDeleteConfirmOpen(true);

  const performDelete = async () => {
    setProcessing(true);
    try {
      const response = await quotes.delete(id);
      if (response.success) {
        toast.success(`✅ Quote deleted successfully!`);
        navigate('/quotes');
      } else {
        toast.error(response.error || 'Failed to delete quote');
      }
    } catch (err) {
      console.error('Error deleting quote:', err);
      toast.error('Failed to delete quote');
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout activeTab="quotes" onLogout={onLogout} user={user}>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <FaSpinner className="animate-spin text-4xl text-green-500 mx-auto mb-4" />
            <p className="text-gray-500">Loading quote details...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  if (!quote) {
    return (
      <AdminLayout activeTab="quotes" onLogout={onLogout} user={user}>
        <div className="bg-white rounded-xl p-8 text-center border border-gray-200">
          <div className="text-4xl mb-4">⚠️</div>
          <h2 className="text-xl font-bold text-red-600 mb-2">Quote Not Found</h2>
          <p className="text-gray-500">The quote you're looking for doesn't exist.</p>
          <Link to="/quotes" className="mt-4 inline-block bg-navy text-white px-6 py-2 rounded-lg hover:bg-green hover:text-navy transition-colors">
            <FaArrowLeft className="inline mr-2" /> Back to Quotes
          </Link>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout activeTab="quotes" onLogout={onLogout} user={user}>
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Link to="/quotes" className="p-2 rounded-lg hover:bg-gray-100 transition-colors text-navy">
          <FaArrowLeft size={20} />
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-navy">📄 Quote Details</h1>
          <div className="flex flex-wrap items-center gap-3 mt-1">
            <span className="font-mono font-bold text-navy">{quote.quote_number}</span>
            <span className={getStatusBadge(effectiveQuoteStatus(quote))}>{getStatusLabel(effectiveQuoteStatus(quote))}</span>
            <span className="text-sm text-gray-500">
              Created: {formatDate(quote.created_at)}
            </span>
          </div>
        </div>
        <div className="ml-auto flex flex-wrap gap-2">
          {(quote.status === 'draft' || quote.status === 'sent') && (
            <Link
              to={`/quotes/edit/${quote.id}`}
              className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg text-sm hover:border-yellow-500 hover:text-yellow-500 transition-colors"
            >
              <FaEdit size={14} /> Edit
            </Link>
          )}

          {(quote.status === 'draft' || quote.status === 'rejected' || quote.status === 'cancelled') && (
            <button
              onClick={handleDelete}
              disabled={processing}
              className="flex items-center gap-2 px-4 py-2 border border-red-200 text-red-600 rounded-lg text-sm hover:border-red-500 hover:bg-red-50 transition-colors disabled:opacity-50"
            >
              <FaTrash size={14} /> Delete
            </button>
          )}
        </div>
      </div>

      {/* Quote Info Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Customer & Quote Details */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <h3 className="text-sm font-medium text-gray-500">Customer</h3>
                <p className="font-bold text-navy">{quote.customer_name || 'N/A'}</p>
                {quote.contact_person && (
                  <p className="text-sm text-gray-600">
                    <FaUser className="inline mr-1" size={12} /> {quote.contact_person}
                  </p>
                )}
                {quote.contact_phone && (
                  <p className="text-sm text-gray-600">
                    <FaPhone className="inline mr-1" size={12} /> {quote.contact_phone}
                  </p>
                )}
                {quote.contact_email && (
                  <p className="text-sm text-gray-600">
                    <FaEnvelopeIcon className="inline mr-1" size={12} /> {quote.contact_email}
                  </p>
                )}
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-500">Quote Details</h3>
                <p className="text-sm text-gray-600">Type: {getTypeLabel(quote.quote_type)}</p>
                <p className="text-sm text-gray-600">Payment Terms: {quote.payment_terms || '30 Days'}</p>
                <p className="text-sm text-gray-600">Valid Until: {formatDate(quote.expiry_date)}</p>
                {quote.salesperson?.full_name && (
                  <p className="text-sm text-gray-600">
                    Salesperson: {quote.salesperson.full_name}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* ✅ PHASE 3 / 3.5: Site Inspection Panel */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-navy flex items-center gap-2">
                <FaClipboardCheck className="text-green-500" /> Site Inspection
              </h3>
              {(['draft', 'sent', 'client_request'].includes(quote.status)) && (
                <button
                  onClick={openInspectionModal}
                  className="flex items-center gap-2 px-4 py-2 bg-navy text-white rounded-lg text-sm font-medium hover:bg-green hover:text-navy transition-colors"
                >
                  <FaClipboardCheck size={14} /> Request Inspection
                </button>
              )}
            </div>

            {inspectionsLoading ? (
              <div className="flex items-center justify-center py-6">
                <FaSpinner className="animate-spin text-green-500" />
                <span className="ml-2 text-sm text-gray-500">Loading inspections...</span>
              </div>
            ) : inspections.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-4">
                No site inspection yet. Request one to email a link to the customer or assign an inspector.
              </p>
            ) : (
              <div className="space-y-3">
                {inspections.map((inspection) => (
                  <div key={inspection.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span
                          className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                            inspection.status === 'submitted'
                              ? 'bg-green-100 text-green-700'
                              : inspection.status === 'expired'
                              ? 'bg-yellow-100 text-yellow-700'
                              : inspection.status === 'awaiting_inspector'
                              ? 'bg-purple-100 text-purple-700'
                              : 'bg-blue-100 text-blue-700'
                          }`}
                        >
                          {inspection.status === 'submitted' ? '✅ Submitted'
                            : inspection.status === 'expired' ? '⚠️ Expired'
                            : inspection.status === 'awaiting_inspector' ? '👷 Awaiting Inspector'
                            : '⏳ Pending'}
                        </span>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                          inspection.type === 'standard' ? 'bg-gray-100 text-gray-600'
                          : inspection.type === 'custom' ? 'bg-orange-100 text-orange-600'
                          : 'bg-purple-100 text-purple-700'
                        }`}>
                          {inspection.type === 'standard' ? 'Standard' : inspection.type === 'custom' ? 'Custom' : 'Inspector Required'}
                        </span>
                        <span className="text-xs text-gray-500">
                          {inspection.origin === 'client_request' ? 'Client provided upfront' : 'Admin requested'}
                        </span>
                      </div>
                      <button
                        onClick={() => handleDeleteInspection(inspection)}
                        className="text-red-500 text-xs hover:text-red-700"
                      >
                        Delete
                      </button>
                    </div>

                    <p className="text-xs text-gray-500">
                      Sent to: <span className="text-gray-700 font-medium">{inspection.email_sent_to || 'N/A'}</span>
                    </p>
                    {inspection.inspector_name && (
                      <p className="text-xs text-gray-500">
                        Inspector: <span className="text-gray-700 font-medium">{inspection.inspector_name}</span>
                        {inspection.inspector_phone && ` (${inspection.inspector_phone})`}
                      </p>
                    )}
                    {inspection.target_date && (
                      <p className="text-xs text-gray-500">
                        Target date: <span className="text-gray-700 font-medium">{inspection.target_date}</span>
                      </p>
                    )}
                    {inspection.requested_at && (
                      <p className="text-xs text-gray-500">
                        Requested: {formatDate(inspection.requested_at)}
                      </p>
                    )}
                    {inspection.submitted_at && (
                      <p className="text-xs text-green-600">
                        Submitted: {formatDate(inspection.submitted_at)}
                      </p>
                    )}

                    {/* ✅ Share actions for a saved inspection record - COPY / EMAIL / WHATSAPP */}
                    {inspection.token && inspection.status !== 'submitted' && (
                      <div className="mt-3 bg-gray-50 rounded-lg p-2">
                        <p className="text-[10px] font-medium text-gray-500 mb-1">
                          {inspection.type === 'inspector' ? 'Report Link' : 'Inspection Link'}
                        </p>
                        <div className="flex items-center gap-2 flex-wrap">
                          <code className="text-[11px] text-navy bg-white border border-gray-200 rounded px-2 py-1 flex-1 min-w-0 truncate">
                            {inspection.type === 'inspector' ? getReportLink(inspection) : getInspectionLink(inspection)}
                          </code>
                          <button
                            onClick={() => copyText(inspection.type === 'inspector' ? getReportLink(inspection) : getInspectionLink(inspection))}
                            className="text-xs flex items-center gap-1 bg-navy text-white px-2 py-1 rounded hover:bg-green hover:text-navy transition-colors"
                            title="Copy Link"
                          >
                            <FaCopy size={11} /> Copy
                          </button>
                          {inspection.type === 'inspector' ? (
                            <button
                              onClick={() => whatsappInspectorReportLink(inspection)}
                              className="text-xs flex items-center gap-1 bg-green text-white px-2 py-1 rounded hover:bg-green transition-colors"
                              title="WhatsApp to Inspector"
                            >
                              <FaWhatsapp size={11} /> WhatsApp
                            </button>
                          ) : (
                            <>
                              <button
                                onClick={() => emailInspectionLink(inspection)}
                                className="text-xs flex items-center gap-1 bg-blue-600 text-white px-2 py-1 rounded hover:bg-blue-700 transition-colors"
                                title="Email Link"
                              >
                                <FaEnvelope size={11} /> Email
                              </button>
                              <button
                                onClick={() => whatsappInspectionLink(inspection)}
                                className="text-xs flex items-center gap-1 bg-green text-white px-2 py-1 rounded hover:bg-green transition-colors"
                                title="WhatsApp to Customer"
                              >
                                <FaWhatsapp size={11} /> WhatsApp
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    )}

                    {/* ✅ Site Inspection Complete - view full report on detail page */}
                    {inspection.status === 'submitted' && inspection.token && (
                      <div className="mt-3 flex items-center justify-between bg-green-50 border border-green-200 rounded-lg p-3 flex-wrap gap-2">
                        <div className="flex items-center gap-2">
                          <FaCheckCircle className="text-green-600" />
                          <span className="text-sm font-medium text-green-700">Site Inspection Complete</span>
                        </div>
                        <Link
                          to={`/site-inspection/${inspection.token}`}
                          className="inline-flex items-center gap-2 px-3 py-1.5 bg-navy text-white rounded-lg text-xs font-medium hover:bg-green hover:text-navy transition-colors"
                        >
                          <FaClipboardCheck size={12} /> View Site Inspection
                        </Link>
                      </div>
                    )}



                    {/* Show uploaded files */}
                    {inspection.status === 'submitted' && inspection.file_urls && inspection.file_urls.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {inspection.file_urls.map((url, idx) => (
                          <a
                            key={idx}
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-blue-600 hover:underline bg-blue-50 px-2 py-1 rounded"
                          >
                            📎 File {idx + 1}
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Quote Specifics */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="font-semibold text-navy mb-4">Quote Specifics</h3>
            
            {(quote.quote_type === 'minerals' || quote.quote_type === 'agri') && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Commodity</p>
                  <p className="font-medium text-navy">{quote.commodity || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Tonnage</p>
                  <p className="font-medium text-navy">{quote.tonnage || 0} MT</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Wet / Dry</p>
                  <p className="font-medium text-navy">{quote.wet_or_dry || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Truck Type</p>
                  <p className="font-medium text-navy">{quote.truck_type || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Rate per Ton</p>
                  <p className="font-medium text-navy">{formatCurrency(quote.rate_per_ton)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Distance</p>
                  <p className="font-medium text-navy">{quote.distance_km || 0} km</p>
                </div>
              </div>
            )}

            {quote.quote_type === 'abnormal' && (
              <div>
                <p className="text-sm text-gray-500">Description</p>
                <p className="font-medium text-navy">{quote.description || 'N/A'}</p>
              </div>
            )}

            {quote.quote_type === 'general' && (
              <div>
                <p className="text-sm text-gray-500">Description</p>
                <p className="font-medium text-navy">{quote.description || quote.services || 'N/A'}</p>
              </div>
            )}

            {(quote.origin || quote.destination || quote.route?.pickup_address || quote.route?.delivery_address) && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <h4 className="text-sm font-medium text-gray-600 mb-2">Route</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-gray-500">Pickup</p>
                    <p className="text-sm text-navy">{quote.route?.pickup_address || quote.origin || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Delivery</p>
                    <p className="text-sm text-navy">{quote.route?.delivery_address || quote.destination || 'N/A'}</p>
                  </div>
                  {quote.distance_km && (
                    <div className="col-span-2">
                      <p className="text-xs text-gray-500">Distance</p>
                      <p className="text-sm text-navy">{quote.distance_km} km</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {quote.items && quote.items.length > 0 && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <h4 className="text-sm font-medium text-gray-600 mb-2">Line Items</h4>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="text-left px-3 py-2 text-xs font-semibold text-gray-500">Description</th>
                        <th className="text-center px-3 py-2 text-xs font-semibold text-gray-500">Qty</th>
                        <th className="text-right px-3 py-2 text-xs font-semibold text-gray-500">Unit Price</th>
                        <th className="text-right px-3 py-2 text-xs font-semibold text-gray-500">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {quote.items.map((item, index) => (
                        <tr key={index} className="border-b border-gray-100">
                          <td className="px-3 py-2 text-sm text-navy">{item.description}</td>
                          <td className="px-3 py-2 text-sm text-center text-gray-600">{item.quantity}</td>
                          <td className="px-3 py-2 text-sm text-right text-gray-600">{formatCurrency(item.unit_price)}</td>
                          <td className="px-3 py-2 text-sm text-right font-medium text-navy">{formatCurrency(item.total)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="flex justify-end">
                <div className="w-64 space-y-1">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Subtotal</span>
                    <span className="font-medium">{formatCurrency(quote.total_amount)}</span>
                  </div>
                  {quote.vat_included && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">VAT (15%)</span>
                      <span className="font-medium">{formatCurrency(quote.vat_amount)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-lg font-bold border-t border-gray-200 pt-2">
                    <span className="text-navy">Grand Total</span>
                    <span className="text-green-600">{formatCurrency(quote.grand_total)}</span>
                  </div>
                </div>
              </div>
            </div>

            {quote.comments && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <p className="text-sm text-gray-500">Comments</p>
                <p className="text-sm text-navy">{quote.comments}</p>
              </div>
            )}

            {quote.terms_conditions && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <p className="text-sm text-gray-500">{(quote.purchase_order_number || quote.purchase_order_url) && (
                    <div className='mt-2 p-3 rounded-lg border' style={{ borderColor: '#E8ECEF' }}>
                      <p className='text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1'>Purchase order</p>
                      {quote.purchase_order_number && <p className='text-sm text-navy'>P.O. number: <strong>{quote.purchase_order_number}</strong></p>}
                      {quote.purchase_order_url && (
                        <a href={quote.purchase_order_url} target='_blank' rel='noreferrer' className='text-sm font-medium text-green hover:underline'>View attached P.O.{quote.purchase_order_name ? (' (' + quote.purchase_order_name + ')') : ''}</a>
                      )}
                      {quote.purchase_order_uploaded_by && <p className='text-xs text-gray-400 mt-1'>Provided by {quote.purchase_order_uploaded_by}</p>}
                    </div>
                  )}
                  {quote.terms_accepted_at && (
                    <p className='text-xs text-gray-400 mt-1'>Terms accepted {new Date(quote.terms_accepted_at).toLocaleDateString()}{quote.terms_accepted_name ? (' by ' + quote.terms_accepted_name) : ''}</p>
                  )}
                  Terms & Conditions</p>
                <p className="text-sm text-navy">{quote.terms_conditions}</p>
                {quote.custom_terms && (
                  <div className="mt-2 p-3 bg-gray-50 rounded-lg">
                    <p className="text-sm font-medium text-gray-600">Custom Terms</p>
                    <p className="text-sm text-navy whitespace-pre-wrap">{quote.custom_terms}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Right Column - Actions */}
        <div className="space-y-4">
          <div className="bg-white rounded-xl border border-gray-200 p-6 sticky top-24">
            <h3 className="font-semibold text-navy mb-4">Actions</h3>

            <div className="space-y-3">
              <QuotePDFButton 
                quote={quote}
                buttonText="Generate PDF"
                className="w-full justify-center py-2.5"
                onGenerate={() => toast.info('📄 Generating PDF...')}
              />

              {quote.status !== 'approved' && quote.status !== 'rejected' && (
                <button
                  onClick={handleSendQuote}
                  disabled={processing}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  <FaEnvelope size={14} /> Send Quote
                </button>
              )}

              {/* Expired quote — reopen it (defaults to a fresh 30-day window so it does not
          immediately lapse again). Admin only; the endpoint enforces that too. */}
      {effectiveQuoteStatus(quote) === 'expired' && (
        <button
          onClick={async () => {
            try {
              const r = await fetch(`${API_URL}/quotes/${quote.id}/reopen`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  Authorization: `Bearer ${localStorage.getItem('token')}`,
                },
                body: JSON.stringify({ expiry_date: new Date(Date.now() + 30 * 86400000).toISOString().split('T')[0] }),
              });
              const d = await r.json();
              if (!d.success) throw new Error(d.error || 'Could not reopen the quote');
              toast.success('Quote extended and resent to the client');
              window.location.reload();
            } catch (e) { toast.error(e.message); }
          }}
          className="flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium hover:bg-gray-50"
          style={{ borderColor: '#E8ECEF', color: '#0A1929' }}
        >
          Extend &amp; resend (30 days)
        </button>
      )}

      {(quote.status === 'sent' || quote.status === 'client_accepted') && (
                <button
                  onClick={handleApprove}
                  disabled={processing}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-green text-white rounded-lg text-sm font-medium hover:bg-green transition-colors disabled:opacity-50"
                >
                  <FaCheckCircle size={14} /> Approve & Create Job
                </button>
              )}

              {quote.status === 'approved' && (
                <>
                  {quote.job_id && (
                    <ProformaInvoiceButton
                      job={{
                        id: quote.job_id,
                        quote_id: quote.id,
                        job_number: quote.job_number,
                        start_date: null,
                        client: quote.customer || null,
                      }}
                      onSuccess={fetchQuote}
                    />
                  )}
                  <div className="p-4 bg-green-50 rounded-lg text-center border border-green-200">
                    <FaCheckCircle className="text-3xl text-green-500 mx-auto mb-2" />
                    <p className="text-green-700 font-medium">Quote Approved</p>
                    <p className="text-xs text-green-600">Job created successfully</p>
                    {quote.job_id && (
                      <Link
                        to={`/new-jobs`}
                        className="text-xs text-blue-600 hover:underline mt-2 inline-block"
                      >
                        View Job →
                      </Link>
                    )}
                  </div>
                </>
              )}

              {quote.status === 'rejected' && (
                <div className="p-4 bg-red-50 rounded-lg text-center border border-red-200">
                  <FaTimes className="text-3xl text-red-500 mx-auto mb-2" />
                  <p className="text-red-700 font-medium">Quote Rejected</p>
                </div>
              )}

              {quote.status === 'cancelled' && (
                <div className="p-4 bg-gray-50 rounded-lg text-center border border-gray-200">
                  <FaTimes className="text-3xl text-gray-400 mx-auto mb-2" />
                  <p className="text-gray-600 font-medium">Quote Cancelled</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ✅ Delete Inspection Confirmation Modal */}
      {showDeleteInspectionModal && deleteInspectionTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowDeleteInspectionModal(false)}></div>
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 p-6">
            <div className="text-center mb-4">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <FaTrash className="text-3xl text-red-600" />
              </div>
              <h2 className="text-xl font-bold text-navy">Delete Inspection</h2>
              <p className="text-sm text-gray-500 mt-2">Are you sure you want to delete this site inspection?</p>
              <p className="text-xs text-gray-400 mt-2">
                {deleteInspectionTarget.type === 'inspector' ? 'Inspector report' : 'Inspection'} for {quote?.quote_number || 'this quote'} • {deleteInspectionTarget.email_sent_to || 'N/A'}
              </p>
              <p className="text-xs text-red-500 mt-2">This action cannot be undone.</p>
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowDeleteInspectionModal(false)}
                className="flex-1 px-4 py-2 border border-gray-200 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteInspection}
                disabled={deletingInspection}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {deletingInspection ? (
                  <><FaSpinner className="animate-spin" /> Deleting...</>
                ) : (
                  <><FaTrash size={14} /> Delete</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ✅ PHASE 3.5: Request Inspection Modal (3 types + share buttons) */}
      {showInspectionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-bold text-navy mb-1">📋 Request Site Inspection</h3>
            <p className="text-sm text-gray-500 mb-4">
              {inspectionType === 'inspector'
                ? 'Assign a subcontracted site inspector to do a physical inspection and complete the digital rigging site report.'
                : 'Send a time-limited link to the customer to complete the site inspection online.'}
            </p>

            {requestDone ? (
              // ============ SHARE STEP ============
              <div>
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4 text-center">
                  <FaCheckCircle className="text-3xl text-green-500 mx-auto mb-2" />
                  <p className="text-green-700 font-medium">
                    {inspectionType === 'inspector' ? 'Inspector requirement created!' : 'Inspection link created!'}
                  </p>
                </div>

                {inspectionType === 'inspector' ? (
                  <div className="space-y-2">
                    <button
                      onClick={() => copyText(generatedReportLink)}
                      className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-navy text-white rounded-lg text-sm font-medium hover:bg-green hover:text-navy transition-colors"
                    >
                      <FaCopy size={14} /> Copy Report Link
                    </button>
                    <button
                      onClick={whatsappToInspector}
                      className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-green-500 text-white rounded-lg text-sm font-medium hover:bg-green transition-colors"
                    >
                      <FaWhatsapp size={16} /> WhatsApp Inspector
                    </button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <button
                      onClick={() => copyText(generatedLink)}
                      className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-navy text-white rounded-lg text-sm font-medium hover:bg-green hover:text-navy transition-colors"
                    >
                      <FaCopy size={14} /> Copy Inspection Link
                    </button>
                    <button
                      onClick={whatsappToCustomer}
                      className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-green-500 text-white rounded-lg text-sm font-medium hover:bg-green transition-colors"
                    >
                      <FaWhatsapp size={16} /> WhatsApp Customer
                    </button>
                  </div>
                )}

                <div className="mt-4 flex justify-end">
                  <button
                    onClick={() => setShowInspectionModal(false)}
                    className="px-4 py-2 rounded-lg text-sm font-medium text-gray-600 border border-gray-200 hover:bg-gray-50 transition-colors"
                  >
                    Close
                  </button>
                </div>
              </div>
            ) : (
              // ============ SETUP STEP ============
              <div>
                {/* Type dropdown */}
                <label className="block text-sm font-medium text-gray-700 mb-1">Inspection Type</label>
                <select
                  value={inspectionType}
                  onChange={(e) => setInspectionType(e.target.value)}
                  className="w-full px-4 py-2 rounded-lg border focus:outline-none focus:border-green mb-4"
                  style={{ borderColor: '#E8ECEF' }}
                >
                  <option value="standard">Standard Site Inspection</option>
                  <option value="custom">Custom Site Inspection</option>
                  <option value="inspector">Site Inspector Required</option>
                </select>

                {/* Standard / Custom: email */}
                {inspectionType !== 'inspector' && (
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email to send inspection link to
                    </label>
                    <input
                      type="email"
                      value={inspectionEmail}
                      onChange={(e) => setInspectionEmail(e.target.value)}
                      placeholder="customer@email.com"
                      className="w-full px-4 py-2 rounded-lg border focus:outline-none focus:border-green"
                      style={{ borderColor: '#E8ECEF' }}
                    />
                  </div>
                )}

                {/* Custom: question builder */}
                {inspectionType === 'custom' && (
                  <div className="mb-4 bg-orange-50 rounded-lg p-3">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Add Custom Questions <span className="text-xs text-gray-400">(shown alongside standard questions)</span>
                    </label>
                    <div className="flex gap-2 mb-2">
                      <input
                        type="text"
                        value={customQuestion}
                        onChange={(e) => setCustomQuestion(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCustomQuestion(); } }}
                        placeholder="e.g. Is there a weighbridge on site?"
                        className="flex-1 px-3 py-2 rounded-lg border focus:outline-none focus:border-green text-sm"
                        style={{ borderColor: '#E8ECEF' }}
                      />
                      <button
                        onClick={addCustomQuestion}
                        className="px-3 py-2 bg-orange-500 text-white rounded-lg text-sm hover:bg-orange-600 transition-colors flex items-center gap-1"
                      >
                        <FaPlus size={12} /> Add
                      </button>
                    </div>
                    {customQuestions.length > 0 && (
                      <div className="space-y-1">
                        {customQuestions.map((q, idx) => (
                          <div key={idx} className="flex items-center justify-between bg-white rounded-lg px-3 py-1.5 border border-orange-100">
                            <span className="text-sm text-gray-700">{idx + 1}. {q}</span>
                            <button onClick={() => removeCustomQuestion(idx)} className="text-red-500 text-xs">Remove</button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Inspector: picker + target date */}
                {inspectionType === 'inspector' && (
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Select Site Inspector</label>
                    {inspectors.length === 0 ? (
                      <p className="text-sm text-gray-400 mb-2">
                        No active inspectors found. Add inspectors on the Settings page first.
                      </p>
                    ) : (
                      <select
                        value={selectedInspector}
                        onChange={(e) => setSelectedInspector(e.target.value)}
                        className="w-full px-4 py-2 rounded-lg border focus:outline-none focus:border-green mb-3"
                        style={{ borderColor: '#E8ECEF' }}
                      >
                        <option value="">Select an inspector...</option>
                        {inspectors.map((insp) => (
                          <option key={insp.id} value={insp.id}>
                            {insp.name} — {insp.coverage_area || 'Any'} {insp.telephone ? `(${insp.telephone})` : ''}
                          </option>
                        ))}
                      </select>
                    )}
                    <label className="block text-sm font-medium text-gray-700 mb-2">Target Date <span className="text-xs text-gray-400">(optional)</span></label>
                    <input
                      type="date"
                      value={targetDate}
                      onChange={(e) => setTargetDate(e.target.value)}
                      className="w-full px-4 py-2 rounded-lg border focus:outline-none focus:border-green"
                      style={{ borderColor: '#E8ECEF' }}
                    />
                    <p className="text-xs text-gray-400 mt-2">
                      No email is sent for this type. You'll get Copy Report Link + WhatsApp Inspector buttons after creating.
                    </p>
                  </div>
                )}

                <div className="flex justify-end gap-3 mt-4">
                  <button
                    onClick={() => setShowInspectionModal(false)}
                    className="px-4 py-2 rounded-lg text-sm font-medium text-gray-600 border border-gray-200 hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  {/* 🔧 Fixed: inline brand green (#00A86B) so the button can never be white-on-white */}
                  <button
                    onClick={handleRequestInspection}
                    disabled={requestingInspection}
                    className="px-4 py-2 rounded-lg text-sm font-medium text-white hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center gap-2"
                    style={{ backgroundColor: '#00A86B' }}
                  >
                    {requestingInspection && <FaSpinner className="animate-spin" />}
                    {requestingInspection
                      ? (inspectionType === 'inspector' ? 'Creating...' : 'Creating...')
                      : (inspectionType === 'inspector' ? 'Create Inspector Request' : 'Create Inspection Link')}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      <ConfirmDialog
        isOpen={approveConfirmOpen}
        onClose={() => setApproveConfirmOpen(false)}
        title="Approve Quote"
        message={`Are you sure you want to approve quote ${quote?.quote_number || ''}? This will create a new job.`}
        confirmLabel="Approve"
        onConfirm={performApprove}
      />
      <ConfirmDialog
        isOpen={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        title="Delete Quote"
        message={`Are you sure you want to delete quote ${quote?.quote_number || ''}?`}
        confirmLabel="Delete"
        danger
        onConfirm={performDelete}
      />
    </AdminLayout>
  );
}

export default QuoteDetailPage;